import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Package, Clock, User, FileText, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RestockPage = () => {
  const [products, setProducts] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [restockHistory, setRestockHistory] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productHistory, setProductHistory] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    product_id: '',
    quantity: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prodRes, lowStockRes, historyRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/products/low-stock/list`),
        axios.get(`${API}/restock`)
      ]);
      setProducts(prodRes.data);
      setLowStockProducts(lowStockRes.data);
      setRestockHistory(historyRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load data');
    }
  };

  const fetchProductHistory = async (productId) => {
    try {
      const response = await axios.get(`${API}/restock?product_id=${productId}`);
      setProductHistory(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    fetchProductHistory(product.id);
  };

  const openRestockModal = (product = null) => {
    setFormData({
      product_id: product?.id || '',
      quantity: '',
      notes: ''
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/restock`, {
        product_id: formData.product_id,
        quantity: parseInt(formData.quantity),
        notes: formData.notes
      });
      toast.success('Stock updated successfully');
      setIsModalOpen(false);
      fetchData();
      if (selectedProduct?.id === formData.product_id) {
        fetchProductHistory(formData.product_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restock');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div data-testid="restock-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Restock</h1>
          <p className="text-slate-500 mt-1 font-body">Manage inventory and view restock history</p>
        </div>
        <Button data-testid="add-restock-btn" onClick={() => openRestockModal()}>
          <Plus className="w-4 h-4 mr-2" />
          Add Stock
        </Button>
      </div>

      <Tabs defaultValue="low-stock">
        <TabsList>
          <TabsTrigger value="low-stock" data-testid="tab-low-stock">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Low Stock ({lowStockProducts.length})
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <Clock className="w-4 h-4 mr-2" />
            History
          </TabsTrigger>
          <TabsTrigger value="by-product" data-testid="tab-by-product">
            <Package className="w-4 h-4 mr-2" />
            By Product
          </TabsTrigger>
        </TabsList>

        <TabsContent value="low-stock" className="mt-6">
          {lowStockProducts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-slate-400">
                All products are well stocked!
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {lowStockProducts.map((product) => (
                <Card key={product.id} className="border-amber-200 bg-amber-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-slate-900">{product.name}</h3>
                        <p className="text-sm text-amber-700 mt-1">
                          {product.stock_qty} remaining (threshold: {product.low_stock_threshold})
                        </p>
                      </div>
                      <Button
                        size="sm"
                        data-testid={`quick-restock-${product.id}`}
                        onClick={() => openRestockModal(product)}
                      >
                        Restock
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Recent Restocks</CardTitle>
            </CardHeader>
            <CardContent>
              {restockHistory.length === 0 ? (
                <p className="text-center py-8 text-slate-400">No restock history yet</p>
              ) : (
                <div className="space-y-3">
                  {restockHistory.slice(0, 20).map((restock) => (
                    <div
                      key={restock.id}
                      data-testid={`restock-entry-${restock.id}`}
                      className="flex items-start justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-slate-400" />
                          <span className="font-medium text-slate-900">{restock.product_name}</span>
                          <span className="text-green-600 font-bold">+{restock.quantity}</span>
                        </div>
                        {restock.notes && (
                          <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {restock.notes}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {restock.operator_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(restock.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="by-product" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Select Product</CardTitle>
              </CardHeader>
              <CardContent className="max-h-96 overflow-y-auto">
                <div className="space-y-2">
                  {products.map((product) => (
                    <button
                      key={product.id}
                      data-testid={`select-product-${product.id}`}
                      onClick={() => handleProductSelect(product)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        selectedProduct?.id === product.id
                          ? 'bg-slate-900 text-white'
                          : 'bg-slate-50 hover:bg-slate-100 text-slate-900'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{product.name}</span>
                        <span className={`text-sm ${
                          selectedProduct?.id === product.id ? 'text-slate-300' : 'text-slate-500'
                        }`}>
                          Stock: {product.stock_qty}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg">
                  {selectedProduct ? `${selectedProduct.name} History` : 'Restock History'}
                </CardTitle>
                {selectedProduct && (
                  <Button
                    size="sm"
                    onClick={() => openRestockModal(selectedProduct)}
                  >
                    Add Stock
                  </Button>
                )}
              </CardHeader>
              <CardContent className="max-h-96 overflow-y-auto">
                {!selectedProduct ? (
                  <p className="text-center py-8 text-slate-400">Select a product to view history</p>
                ) : productHistory.length === 0 ? (
                  <p className="text-center py-8 text-slate-400">No restock history for this product</p>
                ) : (
                  <div className="space-y-3">
                    {productHistory.map((restock) => (
                      <div
                        key={restock.id}
                        className="p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-green-600 font-bold">+{restock.quantity}</span>
                          <span className="text-xs text-slate-400">{formatDate(restock.created_at)}</span>
                        </div>
                        {restock.notes && (
                          <p className="text-sm text-slate-600 mt-1">{restock.notes}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">by {restock.operator_name}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent data-testid="restock-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Stock</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="product">Product</Label>
              <Select
                value={formData.product_id}
                onValueChange={(value) => setFormData({ ...formData, product_id: value })}
              >
                <SelectTrigger data-testid="restock-product-select">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} (Current: {product.stock_qty})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity to Add</Label>
              <Input
                id="quantity"
                data-testid="restock-quantity-input"
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                data-testid="restock-notes-input"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="e.g., Delivery from supplier XYZ"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-restock-btn">
                Add Stock
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RestockPage;
